import * as React from 'react';
import type { ValidationIssue } from '../../../types';
import { EditableFieldActions } from './EditableFieldActions';

export interface EditableFieldEditorProps<TValue> {
  value: TValue;
  saving: boolean;
  saveLabel: string;
  cancelLabel: string;
  render: (params: {
    value: TValue;
    onChange: (val: TValue) => void;
    onCommit: (val: TValue) => Promise<void>;
    onCancel: () => void;
    onBlur: () => void;
    errors?: ValidationIssue[];
  }) => React.ReactNode;
  onChange: (val: TValue) => void;
  onCommit: (val: TValue) => Promise<void>;
  onSave: () => void;
  onCancel: () => void;
  /** Validation issues forwarded to the editor render function. */
  errors?: ValidationIssue[];
  /**
   * Hide the floating save/cancel toolbar. Useful for editors that commit
   * themselves via `onCommit` (e.g. native date pickers, selects).
   * @default false
   */
  hideActions?: boolean;
  /** Called when keyboard focus moves to an element outside the editor (e.g. `Tab`). */
  onFocusLeave?: () => void;
  /** The user opened the editor: give it focus if it took none itself. */
  focusOnMount?: boolean;
}

// Clicks inside floating overlays (date picker, dropdowns, dialogs) are
// rendered in portals and therefore technically outside `containerRef`,
// but semantically belong to the editor. Treat them as inside so the
// portal-rendered control can complete its commit gesture.
const FLOATING_SELECTOR =
  '[data-headlessui-portal], [data-headlessui-state], [role="dialog"], [role="listbox"], [role="menu"], [data-floating-ui-portal]';

// Portals focus may move into without leaving the editor; unlike FLOATING_SELECTOR, no in-page
// headless-ui control, so `Tab` to one still leaves.
const FOCUS_PORTAL_SELECTOR =
  '[data-headlessui-portal], [data-floating-ui-portal], [role="listbox"]';

const TABBABLE_SELECTOR =
  'input:not([type="hidden"]), textarea, select, button, [tabindex]:not([tabindex="-1"])';

export function EditableFieldEditor<TValue>({
  value,
  saving,
  saveLabel,
  cancelLabel,
  render,
  errors,
  onChange,
  onCommit,
  onSave,
  onCancel,
  hideActions = false,
  onFocusLeave,
  focusOnMount = false
}: EditableFieldEditorProps<TValue>) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  // Keep ref to latest callback so the mousedown handler is never stale.
  const onSaveRef = React.useRef(onSave);
  React.useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  React.useEffect(() => {
    // Editors that commit themselves via `onCommit` (selects, date pickers,
    // composite forms) must NOT auto-save on outside-click — the user never
    // explicitly confirmed the value by clicking outside, and for always-active
    // composite rows this listener would fire for every click anywhere on the page.
    if (hideActions) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Don't cancel mid-save: an in-flight commit owns the editor until it
      // resolves or rejects, and rejection should keep the user in edit mode.
      if (saving) return;
      const target = event.target as Element | null;
      if (!containerRef.current) return;
      if (containerRef.current.contains(target)) return;
      if (target?.closest(FLOATING_SELECTOR)) return;
      // Save current value instead of discarding — clicking outside acts as confirm.
      onSaveRef.current();
    };

    // Add a small delay to avoid immediate cancellation when opening edit mode
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [saving, hideActions]);

  // The display that had focus is gone; an editor that took no focus itself (e.g. a select) gets
  // it, so the keyboard keeps working in it. Runs after the editor's own autofocus.
  React.useEffect(() => {
    if (!focusOnMount) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const first = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR) ?? []
    ).find(element => element.tabIndex >= 0 && !element.matches(':disabled'));
    first?.focus();
    // Mount only: whether the user opened the editor is decided as it mounts.
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (saving) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.altKey &&
      !event.metaKey &&
      !event.ctrlKey
    ) {
      // Self-committing editors (modals, selects, date pickers) own their own
      // commit and keyboard handling — persisting the parent's `value` here
      // would overwrite their edits with the stale original. Let Enter reach them.
      if (hideActions) return;
      // Let multiline editors (TEXTAREA) keep Enter as newline; intercept on
      // single-line inputs so keyboard users can commit without the toolbar.
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toUpperCase();
      if (tag === 'TEXTAREA') return;
      event.preventDefault();
      onSave();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!onFocusLeave || saving) return;
    const next = event.relatedTarget as Element | null;
    // Focus lost to nothing focusable is a click outside, which the mousedown handler owns.
    if (!next) return;
    if (containerRef.current?.contains(next) || next.closest(FOCUS_PORTAL_SELECTOR)) return;
    onFocusLeave();
  };

  return (
    <div ref={containerRef} className='relative' onKeyDown={handleKeyDown} onBlur={handleBlur}>
      {render({
        value,
        onChange,
        onCommit,
        onCancel,
        onBlur: () => {}, // No-op - we handle outside clicks with useEffect
        errors
      })}
      {!hideActions && (
        <EditableFieldActions
          saving={saving}
          saveLabel={saveLabel}
          cancelLabel={cancelLabel}
          onSave={onSave}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}

EditableFieldEditor.displayName = 'EditableFieldEditor';
